import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MessageSquare, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useDataCoachChat } from '../DataCoachChatContext';

const ConversationItem = ({
  title,
  preview,
  isSelected,
  onClick,
}: {
  title: string;
  preview: string;
  isSelected: boolean;
  onClick: () => void;
}) => (
  <Box
    _hover={{ bg: 'whiteAlpha.200' }}
    bg={isSelected ? 'whiteAlpha.200' : 'transparent'}
    borderRadius="md"
    cursor="pointer"
    p={3}
    transition="background 0.2s"
    onClick={onClick}
  >
    <Flex align="flex-start" gap={3}>
      <Icon as={MessageSquare} color="cyan.400" mt={1} />
      <Box flex={1} minW={0}>
        <Text isTruncated fontSize="sm" fontWeight="medium" mb={1}>
          {title}
        </Text>
        <Text isTruncated color="whiteAlpha.600" fontSize="xs">
          {preview}
        </Text>
      </Box>
    </Flex>
  </Box>
);

export const ConversationsSidebar = () => {
  const {
    conversations,
    selectedConversation,
    selectConversation,
    createNewConversation,
  } = useDataCoachChat();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredConversations = conversations.filter(
    (conv) =>
      conv.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.preview.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Flex
      bg="bg.main"
      borderRight="1px solid"
      borderRightColor="border.divider"
      direction="column"
      h="100%"
      w="280px"
    >
      {/* Header */}
      <Flex
        align="center"
        borderBottom="1px solid"
        borderBottomColor="border.divider"
        h="49.5px"
        justify="space-between"
        px={4}
      >
        <Text fontSize="lg" fontWeight="semibold">
          Conversations
        </Text>
        <Button
          colorScheme="primaryButton"
          leftIcon={<Plus size={16} />}
          size="xs"
          variant="ghost"
          onClick={createNewConversation}
        >
          New
        </Button>
      </Flex>

      {/* Search */}
      <Box p={4} pb={2}>
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none">
            <Icon as={Search} color="whiteAlpha.400" />
          </InputLeftElement>
          <Input
            bg="input.bg"
            borderColor="border.box"
            borderRadius="md"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>
      </Box>

      {/* Conversations List */}
      <VStack align="stretch" flex={1} overflowY="auto" p={2} spacing={1}>
        {filteredConversations.map((conversation) => (
          <ConversationItem
            key={conversation.id}
            isSelected={selectedConversation?.id === conversation.id}
            preview={conversation.preview}
            title={conversation.title}
            onClick={() => selectConversation(conversation.id)}
          />
        ))}
      </VStack>
    </Flex>
  );
};
